import ms from 'ms';
import { ethers } from 'ethers';
import { isAddressEqual } from '@lib/utils';

export function isValidNonce(nonce: string) {
  const pastTime = Date.now() - new Date(Number(nonce || 0)).getTime();
  return pastTime <= ms('60s');
}

// message 的最后一行是 nonce
export function extractNonce(message: string) {
  const chunks = message.split('\n');
  const nonce = chunks[chunks.length - 1];
  return nonce;
}

export function isValidSignature(
  message: string,
  signature: string,
  publicAddress: string,
): { isValid: boolean; reason?: string } {
  const recoveredAddress = ethers.utils.verifyMessage(message, signature);
  if (isAddressEqual(recoveredAddress, publicAddress)) {
    if (isValidNonce(extractNonce(message))) {
      return {
        isValid: true,
      };
    } else {
      return {
        isValid: false,
        reason:
          'Nonce Invalid, probably a signature timeout, please try again.',
      };
    }
  } else {
    return {
      isValid: false,
      reason: 'Wallet address does not match signature',
    };
  }
}

// 签名与地址不匹配时，推测钱包实际签了什么，用于排查兼容问题
export function diagnoseSignatureMismatch(
  message: string,
  signature: string,
  publicAddress: string,
) {
  const { utils } = ethers;
  const bytes = utils.toUtf8Bytes(message);
  const prefixed = (len: number) =>
    utils.keccak256(
      utils.concat([
        utils.toUtf8Bytes(`\x19Ethereum Signed Message:\n${len}`),
        bytes,
      ]),
    );
  const digests: Record<string, string> = {
    // 把 hex 编码后的消息当成文本签名
    hexAsText: utils.hashMessage(utils.hexlify(bytes)),
    // 前缀长度用了字符数而不是 UTF-8 字节数（消息含中文时两者不同）
    charLengthPrefix: prefixed(message.length),
    // 没加 EIP-191 前缀
    noPrefix: utils.keccak256(bytes),
    crlf: utils.hashMessage(message.replace(/\n/g, '\r\n')),
    // UTF-8 字节被当成 Latin-1 字符，又编码了一次
    doubleEncoded: utils.hashMessage(String.fromCharCode(...Array.from(bytes))),
  };
  const matchedVariant = Object.keys(digests).find((key) => {
    try {
      return isAddressEqual(
        utils.recoverAddress(digests[key], signature),
        publicAddress,
      );
    } catch {
      return false;
    }
  });
  return {
    signatureBytes: utils.arrayify(signature).length,
    // 都不匹配时，最可能是钱包用另一个账户签了名
    matchedVariant: matchedVariant || null,
  };
}

export function generateMessage(publicAddress: string) {
  const message = `Please sign to confirm that you are the owner of the following address.\n请签名以确认你是以下地址的持有者\n\nAddress\n${publicAddress}\n\nNonce\n${Date.now()}`;
  return message;
}
